'use client';

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Check,
  Plus,
  Folder,
  Star,
  Clock,
  CheckSquare,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { useCollections } from '@/lib/context/CollectionsContext';

interface SaveToCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  momentData: {
    momentId?: string;
    postId: string;
    startTime: number;
    endTime: number;
    title?: string;
    description?: string;
    addedFromSearch?: boolean;
    searchQuery?: string;
  };
  onSaved?: () => void;
}

const ICON_MAP: Record<string, React.ReactNode> = {
  star: <Star className="h-4 w-4" />,
  clock: <Clock className="h-4 w-4" />,
  'check-square': <CheckSquare className="h-4 w-4" />,
  folder: <Folder className="h-4 w-4" />,
};

export function SaveToCollectionDialog({
  open,
  onOpenChange,
  momentData,
  onSaved,
}: SaveToCollectionDialogProps) {
  const { collections, refetchCollections, createCollection, activeDomainId } = useCollections();
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      checkExistingCollections();
    }
  }, [open, momentData.momentId, momentData.postId, momentData.startTime]);

  const checkExistingCollections = async () => {
    try {
      const response = await fetch(
        `/api/collections/check-moment?${new URLSearchParams({
          momentId: momentData.momentId || '',
          postId: momentData.postId,
          startTime: momentData.startTime.toString(),
        })}`
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedCollections(new Set(data.collectionIds || []));
      }
    } catch (error) {
      console.error('[SaveToCollection] Error checking existing collections:', error);
    }
  };

  const toggleCollection = (collectionId: string) => {
    setSelectedCollections(prev => {
      const next = new Set(prev);
      if (next.has(collectionId)) {
        next.delete(collectionId);
      } else {
        next.add(collectionId);
      }
      return next;
    });
  };

  const handleCreateCollection = async () => {
    if (!newCollectionName.trim()) {
      toast.error('Please enter a collection name');
      return;
    }

    const collection = await createCollection(newCollectionName.trim());
    if (collection) {
      toast.success('Collection created');
      setNewCollectionName('');
      setIsCreating(false);
      toggleCollection(collection.id);
    } else {
      toast.error('Failed to create collection');
    }
  };

  const handleSave = async () => {
    if (selectedCollections.size === 0) {
      toast.error('Please select at least one collection');
      return;
    }

    setIsSaving(true);

    try {
      const results = await Promise.allSettled(
        Array.from(selectedCollections).map(async (collectionId) => {
          const response = await fetch(`/api/collections/${collectionId}/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              moment_id: momentData.momentId,
              post_id: momentData.postId,
              start_time: momentData.startTime,
              end_time: momentData.endTime,
              title: momentData.title,
              description: momentData.description,
              added_from_search: momentData.addedFromSearch,
              search_query: momentData.searchQuery,
            }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to save');
          }

          return response.json();
        })
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (succeeded > 0) {
        toast.success(`Saved to ${succeeded} collection${succeeded > 1 ? 's' : ''}`);
        onSaved?.();
        onOpenChange(false);
      }

      if (failed > 0) {
        toast.error(`Failed to save to ${failed} collection${failed > 1 ? 's' : ''}`);
      }
    } catch (error) {
      console.error('[SaveToCollection] Error saving:', error);
      toast.error('Failed to save to collections');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Save to Collection</DialogTitle>
          <DialogDescription>
            Choose one or more collections to save this moment
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isCreating ? (
            <div className="space-y-2">
              <Input
                placeholder="New collection name..."
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCollection();
                  } else if (e.key === 'Escape') {
                    setIsCreating(false);
                    setNewCollectionName('');
                  }
                }}
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleCreateCollection}
                  disabled={!newCollectionName.trim()}
                >
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsCreating(false);
                    setNewCollectionName('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Collection
            </Button>
          )}

          {collections.length > 0 && (
            <>
              <Separator />
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {collections.map((collection) => {
                    const isSelected = selectedCollections.has(collection.id);
                    const icon = ICON_MAP[collection.icon || 'folder'] || ICON_MAP.folder;

                    return (
                      <button
                        key={collection.id}
                        onClick={() => toggleCollection(collection.id)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-lg border transition-all',
                          'hover:bg-muted/50',
                          isSelected && 'bg-primary/10 border-primary'
                        )}
                      >
                        <div
                          className="flex items-center justify-center h-8 w-8 rounded-md"
                          style={{ backgroundColor: collection.color || '#8B5CF6' }}
                        >
                          <div className="text-white">
                            {icon}
                          </div>
                        </div>

                        <div className="flex-1 text-left">
                          <div className="font-medium text-sm">{collection.name}</div>
                          {collection.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {collection.description}
                            </div>
                          )}
                        </div>

                        {isSelected && (
                          <Check className="h-5 w-5 text-primary" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </ScrollArea>
            </>
          )}

          {collections.length === 0 && !isCreating && (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No collections yet. Create one to get started!
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={selectedCollections.size === 0 || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              `Save to ${selectedCollections.size} collection${selectedCollections.size !== 1 ? 's' : ''}`
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
